import { useState } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '../../../components/ui/card';
import { DataTable, DataTableColumn } from '../../../components/ui/data-table';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { ShieldCheck, Check, X, Eye, Filter, Download } from 'lucide-react';
import PageHeader from '../../../components/common/PageHeader';
import TradingStatusBadge from '../../../components/trading/TradingStatusBadge';

type ApprovalRequest = {
  id: string;
  type: 'strategy' | 'manager' | 'investor';
  requestId: string;
  requestName: string;
  submittedBy: string;
  submittedDate: string;
  status: 'pending' | 'approved' | 'rejected';
  details: string;
  documents?: string[];
};

const PAMMApprovals = () => {
  const [searchTerm, setSearchTerm] = useState('');

  const approvals: ApprovalRequest[] = [
    {
      id: 'APR001',
      type: 'strategy',
      requestId: 'STR004',
      requestName: 'Crypto Momentum',
      submittedBy: 'Mike Wilson',
      submittedDate: '2024-01-20',
      status: 'pending',
      details: 'New strategy application with 15% target ROI',
      documents: ['strategy_plan.pdf', 'risk_analysis.pdf'],
    },
    {
      id: 'APR002',
      type: 'manager',
      requestId: 'MGR004',
      requestName: 'Sarah Connor',
      submittedBy: 'Sarah Connor',
      submittedDate: '2024-01-19',
      status: 'pending',
      details: 'Manager verification request',
      documents: ['id_proof.pdf', 'experience_proof.pdf'],
    },
    {
      id: 'APR003',
      type: 'investor',
      requestId: 'INV004',
      requestName: 'Large Investment Request',
      submittedBy: 'Tom Anderson',
      submittedDate: '2024-01-18',
      status: 'approved',
      details: 'Request to invest $100,000 in Gold Scalper Pro',
    },
  ];

  const columns: DataTableColumn<ApprovalRequest>[] = [
    {
      id: 'id',
      header: 'Request ID',
      accessor: row => <span className="font-medium">{row.id}</span>,
      sortable: true,
      sortValue: row => row.id,
      width: '120px',
    },
    {
      id: 'type',
      header: 'Type',
      accessor: row => (
        <Badge
          className={
            row.type === 'strategy'
              ? 'bg-blue-100 text-blue-800'
              : row.type === 'manager'
                ? 'bg-purple-100 text-purple-800'
                : 'bg-green-100 text-green-800'
          }
        >
          {row.type.charAt(0).toUpperCase() + row.type.slice(1)}
        </Badge>
      ),
      sortable: true,
      sortValue: row => row.type,
      width: '120px',
    },
    {
      id: 'requestName',
      header: 'Request Name',
      accessor: row => row.requestName,
      sortable: true,
      sortValue: row => row.requestName,
      width: '200px',
    },
    {
      id: 'submittedBy',
      header: 'Submitted By',
      accessor: row => row.submittedBy,
      sortable: true,
      sortValue: row => row.submittedBy,
      width: '180px',
    },
    {
      id: 'submittedDate',
      header: 'Submitted Date',
      accessor: row => new Date(row.submittedDate).toLocaleDateString(),
      sortable: true,
      sortValue: row => row.submittedDate,
      width: '140px',
    },
    {
      id: 'status',
      header: 'Status',
      accessor: row => <TradingStatusBadge status={row.status} />,
      sortable: true,
      sortValue: row => row.status,
      width: '120px',
    },
    {
      id: 'actions',
      header: 'Actions',
      accessor: row => (
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => console.log('View:', row.id)}
          >
            <Eye className="h-4 w-4" />
          </Button>
          {row.status === 'pending' && (
            <>
              <Button
                variant="ghost"
                size="sm"
                className="text-green-600"
                onClick={() => console.log('Approve:', row.id)}
              >
                <Check className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="text-red-600"
                onClick={() => console.log('Reject:', row.id)}
              >
                <X className="h-4 w-4" />
              </Button>
            </>
          )}
        </div>
      ),
      width: '160px',
    },
  ];

  const filteredApprovals = approvals.filter(approval => {
    return (
      searchTerm === '' ||
      approval.requestName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      approval.submittedBy.toLowerCase().includes(searchTerm.toLowerCase()) ||
      approval.id.toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  const pendingApprovals = approvals.filter(a => a.status === 'pending').length;
  const approvedToday = approvals.filter(a => a.status === 'approved').length;
  const rejectedToday = approvals.filter(a => a.status === 'rejected').length;

  return (
    <div className="space-y-6">
      <div className="sticky top-0 z-20 bg-background pt-4">
        <PageHeader
          title="Approval & Verification"
          description="Review and approve PAMM strategy, manager, and investor requests"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="flex items-center gap-2 p-4 bg-yellow-50 rounded-lg">
          <ShieldCheck className="h-5 w-5 text-yellow-600" />
          <div>
            <p className="text-sm text-muted-foreground">Pending Approvals</p>
            <p className="text-2xl font-bold text-yellow-600">
              {pendingApprovals}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 p-4 bg-green-50 rounded-lg">
          <Check className="h-5 w-5 text-green-600" />
          <div>
            <p className="text-sm text-muted-foreground">Approved Today</p>
            <p className="text-2xl font-bold text-green-600">{approvedToday}</p>
          </div>
        </div>

        <div className="flex items-center gap-2 p-4 bg-red-50 rounded-lg">
          <X className="h-5 w-5 text-red-600" />
          <div>
            <p className="text-sm text-muted-foreground">Rejected Today</p>
            <p className="text-2xl font-bold text-red-600">{rejectedToday}</p>
          </div>
        </div>

        <div className="flex items-center gap-2 p-4 bg-blue-50 rounded-lg">
          <ShieldCheck className="h-5 w-5 text-blue-600" />
          <div>
            <p className="text-sm text-muted-foreground">Total Requests</p>
            <p className="text-2xl font-bold text-blue-600">
              {approvals.length}
            </p>
          </div>
        </div>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Approval Requests</CardTitle>
              <CardDescription>
                Review and manage pending approval requests
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm">
                <Filter className="h-4 w-4 mr-2" />
                Filters
              </Button>
              <Button variant="outline" size="sm">
                <Download className="h-4 w-4 mr-2" />
                Export
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <DataTable
            columns={columns}
            data={filteredApprovals}
            searchPlaceholder="Search by request name, submitter, or ID..."
            onSearchChange={setSearchTerm}
            showColumnVisibility={true}
            showExportButton={false}
            showAddButton={false}
          />
        </CardContent>
      </Card>
    </div>
  );
};

export default PAMMApprovals;
